<template>
  <div class="flex flex-col gap-y-8 h-auto relative">
    <!-- Loading overlay -->
    <div
      v-if="isLoading"
      class="absolute inset-0 bg-surface bg-opacity-75 flex items-center justify-center z-50 rounded-lg"
    >
      <div class="flex flex-col items-center gap-3">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <span class="text-ink-soft font-medium">Updating order...</span>
      </div>
    </div>

    <div class="flex justify-center items-center" v-if="isQuoteOrder || showProvideQuoteButton">
      <template v-if="isQuoteOrder">
        <div class="fs-4 font-weight-bolder order-title p-2">Quote pending</div>
      </template>

      <div
        class="font-bold flex order-title p-2"
        v-if="(isQuoteOrder && showQuoteButtons) || showProvideQuoteButton"
      >
        <template v-if="isQuoteOrder && showQuoteButtons">
          <button
            class="btn btn-secondary mx-2 cursor-pointer fs-2"
            @click="provideQuoteModal = true"
          >
            Accept Quote
          </button>
          <button class="btn btn-danger mx-2 cursor-pointer fs-2" @click="handleRejectQuote()">
            Reject Quote
          </button>
        </template>

        <button
          v-if="showProvideQuoteButton"
          class="btn btn-secondary mx-2 cursor-pointer fs-2"
          @click="handleProvideQuotePrice()"
        >
          Provide Quote
        </button>
      </div>
    </div>
    <TimeLine :orderStatus="order_item?.status" :is-order-status="true" />

    <!-- One line under the strip: "Move order" opens the grouped picker with
         the reason inside it (AB#21303). Same refs, same moveOrderStatus(). -->
    <MoveOrderPicker
      v-if="can('move-order')"
      :options="moveOptionsFor(order_item)"
      :current-status="order_item?.status ?? ''"
      :current-status-text="CustimooOrderFlowStatuses[order_item?.status ?? ''] ?? ''"
      :order-no="order_no ?? ''"
      :status="getSelectedMoveStatusValue() || null"
      :reason="move_order_reason"
      :busy="isMovingOrder || showShippingModal"
      :disabled="!order_item?.factory_id || order_item?.status === 'on_hold'"
      :disabled-reason="
        !order_item?.factory_id
          ? 'Order cannot be moved until it is assigned to a factory.'
          : order_item?.status === 'on_hold'
            ? 'Order is on hold and cannot be moved.'
            : ''
      "
      @update:status="(v) => (move_order_to_status = v)"
      @update:reason="(v) => (move_order_reason = v)"
      @move="moveOrderStatus(order_item, order_item_index)"
    />

    <!-- Enhanced Timeline Implementation -->
    <TimeLine
      :orderStatus="order_item?.status"
      :isOrderStatus="false"
      :current-step="1"
      :total-items="allActivitiesCount"
      :direction="'vertical'"
      :alternating="false"
    >
      <TimeLineItem
        v-for="(status_activity, isaIdx) in order_item?.status_activities"
        :key="`timeline-item-${isaIdx}_${remainingCount}_${notes.toString()}`"
        :attribution="getAttributionHtml(status_activity)"
        :title="activityStatus[status_activity.status]?.title"
        :index="isaIdx"
        :total-items="allActivitiesCount"
        :alternating="false"
        :activity-date-time="
          formatTimestampWithBrowserTimezone(status_activity.created_at, status_activity.timezone)
        "
        :icon="getActivityIcon(status_activity.status)"
        :completed-icon="getCompletedIcon(status_activity.status)"
        :action-log="status_activity.action_log"
        :activity-user="status_activity.user"
      >
        <TimeLineItemContent
          :activity_content="
            getActivityContent(status_activity.activity_items, status_activity.status)
          "
          :reference_no="
            props.order.additional_fields &&
            props.order.additional_fields.is_manual_order &&
            isaIdx === 0
              ? props.order?.customer_reference_no
              : null
          "
          :order_item_id="order_item?.id"
          @show-preview="
            (image_url) => {
              showPreview(
                status_activity.activity_items[
                  getActivityItemIndex(status_activity.activity_items, image_url)
                ]
              );
            }
          "
        >
          <p
            class="rounded-full px-2 py-1 mb-2 w-max text-sm text-white"
            v-if="
              shipmentStatus &&
              (status_activity.status === ORDERSHIPPED ||
                status_activity.status === ORDERINPRODUCTION)
            "
            :class="{
              'bg-[#d0b440]': shipmentStatus === 'Partially Shipped',
              'bg-[var(--success)]': shipmentStatus === 'Shipped',
            }"
          >
            {{ shipmentStatus }}
          </p>
          <!-- TimeLine top actions -->
          <div class="flex gap-1 flex-wrap mb-2">
            <!-- Your existing timeline content... -->
            <span
              class="flex gap-1"
              v-if="
                ((order_item?.status === ORDERINPRODUCTION &&
                  status_activity.status === ORDERINPRODUCTION &&
                  !order_item?.est_shipping_date) ||
                  (order_item?.status === ORDERINPRODUCTION &&
                    status_activity.status === QUALITYCONTROL)) &&
                isaIdx === currentInProductionOrQCIndex
              "
            >
              <button
                v-if="can('enter-shipping-date') || order_item.factory_name === 'Custom Product'"
                @click="() => handleEnterShippingDate(order_item)"
                class="bg-primary px-3 py-2 text-white rounded-md hover:bg-primary-dark transition-colors !cursor-pointer flex gap-2"
              >
                <CalendarIcon class="h-5 w-5 text-white" />
                Enter Est. Shipping Date
              </button>
              <button
                v-if="can('enter-shipping-date') || order_item.factory_name === 'Custom Product'"
                @click="() => (enterShippingDetails = true)"
                class="ml-2 bg-primary px-3 py-2 text-white rounded-md hover:bg-primary-dark transition-colors !cursor-pointer flex gap-2"
              >
                <TruckIcon class="h-5 w-5 text-white" />
                Enter Shipping Details
              </button>
            </span>
          </div>
          <!-- Other action buttons... -->
          <template
            v-if="
              (order_item?.status === FACTORYAPPROVED &&
                status_activity.status === FACTORYAPPROVED) ||
              (order_item?.status == CUSTOMERREVIEW && status_activity.status == CUSTOMERREJECTED)
            "
          >
            <button
              @click="uploadSampleFiles(order_item, isaIdx, status_activity)"
              class="px-3 py-2 bg-surface-3 hover:bg-surface-3 rounded-md flex items-center gap-2"
            >
              <ArrowUpOnSquareIcon class="size-5" />
              Upload Design Sample
            </button>
          </template>
          <div class="flex items-center w-full justify-between">
            <div class="flex items-center gap-2">
              <button
                v-if="
                  allowCreateDeliveryNote &&
                  (status_activity.status === ORDERSHIPPED ||
                    status_activity.status === ORDERINPRODUCTION)
                "
                @click="openDeliveryNotes(null)"
                class="px-3 py-2 bg-surface-3 hover:bg-surface-3 rounded-md flex items-center gap-2"
              >
                <PlusIcon class="size-5" />
                Create Delivery Note
              </button>
              <button
                v-if="order_item?.status == ORDERSHIPPED && status_activity.status == ORDERSHIPPED"
                class="px-3 py-2 bg-surface-3 hover:bg-surface-3 rounded-md flex items-center gap-2 w-max"
                @click="makeActivityData(order_item, order_item_index, ORDERCOMPLETED, false)"
              >
                <ArrowUpRightIcon class="size-5" />
                Complete Order
              </button>
            </div>
            <!-- <template> -->
            <button
              v-if="
                status_activity.status === ORDERSHIPPED &&
                order_item?.tracking_no &&
                order_item?.tracking_response?.history?.length > 0
              "
              class="px-3 py-2 bg-surface-3 hover:bg-surface-3 rounded-md flex items-center gap-2 w-max"
              @click="showShippedOrderModal = true"
            >
              View Journey
            </button>
            <!-- </template> -->
          </div>

          <template
            v-if="order_item?.status == FACTORYREVIEW && status_activity.status == FACTORYREVIEW"
          >
            <div class="flex items-center gap-2">
              <button
                @click="
                  makeActivityData(order_item, order_item_index, ORDERAPPROVE, true, null, true)
                "
                class="px-3 py-2 bg-surface-3 hover:bg-surface-3 rounded-md flex items-center gap-2"
              >
                <BuildingStorefrontIcon class="size-5" />
                Mark to factory
              </button>
              <button
                @click="
                  makeActivityData(order_item, order_item_index, ORDERCANCEL, false, null, true)
                "
                class="px-3 py-2 bg-surface-3 hover:bg-surface-3 rounded-md flex items-center gap-2"
              >
                <ExclamationCircleIcon class="size-5" />
                Cancel Order
              </button>
            </div>
          </template>

          <template
            v-if="
              can('update-order') &&
              order_item?.status == ORDERAPPROVE &&
              status_activity.status == ORDERAPPROVE &&
              order_item.factory_id &&
              isaIdx === 0
            "
          >
            <button
              @click="showArtworkDesignsAndReview(order_item, order_item_index, isaIdx)"
              class="px-3 py-2 bg-surface-3 hover:bg-surface-3 rounded-md flex items-center gap-2"
            >
              <ArrowUpOnSquareIcon class="size-5" />
              Review Artwork
            </button>
          </template>
          <div
            class="actions"
            v-if="
              can('review-samples') &&
              isaIdx === 0 &&
              order_item.status == CUSTOMERREVIEW &&
              status_activity.status == CUSTOMERREVIEW
            "
          >
            <button
              class="px-3 py-2 bg-surface-3 hover:bg-surface-3 rounded-md flex items-center gap-2"
              @click="showSampleDesigns(order_item_index, isaIdx)"
            >
              Review Samples
            </button>
          </div>
          <div
            v-if="
              ((order_item?.status === CUSTOMERAPPROVED &&
                status_activity.status === CUSTOMERAPPROVED) ||
                (order_item?.status === PRODUCTIONFILESUPLOADED &&
                  status_activity.status === PRODUCTIONFILESUPLOADED)) &&
              can('put-order-in-production') &&
              !isPutInProduction
            "
            class="mb-4"
          >
            <button
              @click="() => handlePutInProduction(order_item)"
              class="px-3 py-2 bg-surface-3 hover:bg-surface-3 rounded-md flex items-center gap-2"
            >
              <svg
                viewBox="0 0 16 16"
                width="1em"
                height="1em"
                focusable="false"
                role="img"
                aria-label="printer"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                class="bi-printer"
              >
                <g>
                  <path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"></path>
                  <path
                    d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2H5zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4V3zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1z"
                  ></path>
                </g>
              </svg>
              Put In Production
            </button>
          </div>
          <!-- </div> -->

          <!-- Your existing content sections... -->
          <div
            v-if="status_activity.status === ORDERINPRODUCTION && order_item?.est_shipping_date"
            class="bg-surface-2 p-5 rounded-md border border-line text-xs"
          >
            <div class="flex justify-between">
              <div class="flex flex-col-reverse">
                <ul class="relative">
                  <li
                    class="shipping-dates-list"
                    v-for="(date, date_idx) in reversedEstShippingDates(order_item)"
                    :key="`shipping_date_${date_idx}`"
                  >
                    <span
                      class="text-muted"
                      v-if="date_idx == order_item?.est_shipping_date.length - 1"
                      ><span class="font-medium">Estimated shipping date: </span>
                      <span class="text-ink-mute">{{ date }}</span></span
                    >
                    <span class="text-muted" v-else
                      ><span class="font-medium">Updated estimated shipping date: </span>
                      <span class="text-ink-mute">{{ date }}</span></span
                    >
                    <div
                      v-if="shippingDateChangeReasonFor(order_item, date)"
                      class="mt-2 pl-3 border-l-2 border-primary/50 text-xs text-ink-soft leading-relaxed"
                    >
                      <span class="font-medium text-ink-soft">Reason for change: </span>
                      {{ shippingDateChangeReasonFor(order_item, date) }}
                    </div>
                  </li>
                </ul>
              </div>
              <div
                class="flex justify-end h-max space-x-2"
                v-if="
                  ((order_item?.status === ORDERINPRODUCTION &&
                    status_activity.status === ORDERINPRODUCTION &&
                    !order_item?.est_shipping_date) ||
                    (order_item?.status === ORDERINPRODUCTION &&
                      status_activity.status !== QUALITYCONTROL)) &&
                  isaIdx === currentInProductionOrQCIndex
                "
              >
                <button
                  v-if="can('enter-shipping-date') || order_item.factory_name === 'Custom Product'"
                  @click="enterShippingDates = true"
                  class="p-2 bg-surface-2 hover:bg-surface-3 transition-colors border border-line rounded-md"
                >
                  <CalendarIcon class="h-5 w-5 text-ink-soft" />
                </button>
                <button
                  v-if="
                    (() => {
                      const hasPermission =
                        can('enter-order-shipping') || order_item.factory_name === 'Custom Product';
                      const statusCondition =
                        (order_item?.status === ORDERINPRODUCTION &&
                          status_activity.status === ORDERINPRODUCTION) ||
                        status_activity.status === QUALITYCONTROL;
                      return hasPermission && statusCondition;
                    })() && isaIdx === currentInProductionOrQCIndex
                  "
                  @click="
                    () => {
                      enterShippingDetails = true;
                    }
                  "
                  class="p-2 bg-surface-2 border border-line rounded-md hover:bg-surface-3 transition-colors"
                >
                  <TruckIcon class="h-5 w-5 text-ink-soft" />
                </button>
              </div>
            </div>
          </div>

          <template
            v-if="
              order_item.id &&
              (status_activity.status === ORDERSHIPPED ||
                status_activity.status === ORDERINPRODUCTION)
            "
          >
            <DeliveryNoteList
              v-if="can('list-delivery-notes')"
              :key="notes.length"
              :order_item_id="order_item.id"
              :isFetching="isFetchingNotes"
              :notesError="notesError"
              :notes="notes"
              @deleted="() => fetchNotes()"
              @edit="(note) => openDeliveryNotes(Number(note?.id) || null)"
              @download-pdf="(note) => downloadDeliveryNotePDF(note?.id)"
            />
          </template>

          <template
            v-for="(activity_item, aiIdx) in status_activity.activity_items"
            :key="`activity-item-${aiIdx}`"
          >
            <div
              v-if="status_activity.activity_items && status_activity.activity_items.length > 0"
              class="grid grid-cols-1"
            >
              <div class="relative" :class="evaluateClass(activity_item, status_activity.status)">
                <div
                  v-if="
                    activity_item.activity_files &&
                    activity_item.activity_files.length > 0 &&
                    status_activity.status === PRODUCTIONFILESUPLOADED
                  "
                  class="flex gap-2 mb-3"
                >
                  <button
                    @click.stop="downloadActivityProductionFile(activity_item.activity_files)"
                    class="px-2 py-1 bg-surface-3 hover:bg-surface-3 rounded text-xs flex items-center gap-1"
                  >
                    <svg style="width: 1em; height: 1em" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
                    </svg>
                    Download
                  </button>
                </div>

                <template
                  v-if="
                    status_activity.status === ORDERSHIPPED &&
                    aiIdx === 0 &&
                    order_item?.tracking_link
                  "
                >
                  <div class="my-2 text-sm w-full p-2 flex gap-2 items-center">
                    <span>
                      The shipping link is
                      <a
                        :href="order_item?.tracking_link"
                        target="_blank"
                        class="text-blue-500 hover:underline"
                      >
                        {{ order_item?.tracking_link }}
                      </a>
                    </span>
                    <Tooltip
                      content="Edit shipping link"
                      class="w-max"
                      v-if="can('update-shipping-link')"
                    >
                      <template #trigger>
                        <button class="btn bg-primary rounded p-1" @click="openTrackingLinkModel">
                          <PencilIcon class="h-3 w-3 text-white" />
                        </button>
                      </template>
                    </Tooltip>
                  </div>
                  <!-- MAWB info — shown when MAWB number is present on the order item -->
                  <div
                    v-if="
                      status_activity.status === ORDERSHIPPED &&
                      aiIdx === 0 &&
                      order_item?.mawb_number
                    "
                    class="my-2 text-sm w-full p-2 bg-surface-2 rounded-md border border-line flex flex-col gap-1"
                  >
                    <span>
                      <span class="font-medium">MAWB:</span> {{ order_item.mawb_number }}
                    </span>
                    <span v-if="order_item.mawb_airline">
                      <span class="font-medium">Airline:</span> {{ order_item.mawb_airline }}
                    </span>
                    <span>
                      <a
                        :href="`https://www.track-trace.com/aircargo?awb=${order_item.mawb_number.replace('-', '')}`"
                        target="_blank"
                        class="text-blue-500 hover:underline"
                      >
                        Track Shipment
                      </a>
                    </span>
                  </div>
                  <template
                    v-if="
                      order_item.tracking_response?.latest_event &&
                      order_item?.tracking_response?.history?.length > 0
                    "
                  >
                    <Transition name="fade">
                      <Accordion :default-open="false" header-color="!bg-surface border-b">
                        <template #header>
                          <div class="grid grid-cols-2 items-center justify-between w-full">
                            <div>
                              <h1>
                                {{
                                  latestEvent.date !== 'N/A' ? formatDate(latestEvent.date) : 'N/A'
                                }}
                              </h1>
                              <span class="text-sm" v-if="latestEvent.date !== 'N/A'">
                                {{ getTimeFromISO(latestEvent.date) }}
                              </span>
                              <span class="text-sm" v-else>N/A</span>
                            </div>

                            <div>
                              <h1
                                :class="[
                                  'font-medium',
                                  getTrackingStatusColor(latestEvent.derived_status),
                                ]"
                              >
                                {{ getTrackingStatus(latestEvent.derived_status) }}
                              </h1>
                              <h1>{{ latestEvent.city }} - {{ latestEvent.country_name }}</h1>
                            </div>
                          </div>
                        </template>
                        <template #content>
                          <div v-if="filteredTrackingHistory?.length">
                            <h1 class="font-bold text-xl mb-4">Tracking History</h1>
                            <div
                              v-for="(item, index) in filteredTrackingHistory"
                              :key="index"
                              class="border-bottom grid grid-cols-2 py-2"
                            >
                              <div><strong>Status:</strong> {{ item.derived_status }}</div>
                              <div><strong>Event:</strong> {{ item.event_description }}</div>
                              <div><strong>City:</strong> {{ item.city || 'N/A' }}</div>
                              <div><strong>Country:</strong> {{ item.country_name }}</div>
                              <div><strong>Date:</strong> {{ formatDate(item.date) }}</div>
                            </div>

                            <div>
                              <h1
                                :class="[
                                  'font-medium',
                                  getTrackingStatusColor(latestEvent.derived_status),
                                ]"
                              >
                                {{ getTrackingStatus(latestEvent.derived_status) }}
                              </h1>
                              <h1>{{ latestEvent.city }} - {{ latestEvent.country_name }}</h1>
                            </div>
                          </div>
                          <div v-else>No tracking history available</div>
                        </template>
                      </Accordion>
                    </Transition>
                  </template>
                </template>
              </div>
            </div>
          </template>

          <template #comments>
            <CommentsList
              :order_item_id="order_item.id"
              :api-url="`/order_item/${status_activity.id}/comment`"
              :current-user="currentUser"
              :can-add-comments="canAddComments"
              :can-edit-comments="canEditComments"
              :can-delete-comments="canDeleteComments"
              :can-resolve-comments="canResolveComments"
              :initial-comments="status_activity.comments || []"
              :storage-url="storageUrl"
              @comments-updated="(comments) => handleCommentsUpdated(status_activity, comments)"
              :isaIdx="isaIdx"
            />
          </template>
        </TimeLineItemContent>
      </TimeLineItem>
    </TimeLine>

    <!-- Your existing modals... -->
    <ShippingDetailsModal
      v-if="shouldShowShippingModal && !showShippingModal"
      :show-shipping-date="Boolean(enterShippingDates)"
      :show-put-in-production="Boolean(isPutInProduction)"
      :show-shipping-details="Boolean(enterShippingDetails || editShippingLink)"
      :est_shipping_date="est_shipping_date"
      :order-item="order_item"
      :order="order"
      :edit-shipping-link="Boolean(editShippingLink)"
      :status="ORDERINPRODUCTION"
      @close="
        () => {
          enterShippingDetails = false;
          enterShippingDates = false;
          isPutInProduction = false;
          editShippingLink = false;
        }
      "
      @shipping-date-saved="handleShippingDateSaved"
      @put-in-production-saved="handlePutInProductionSaved"
      @shipping-details-saved="handleShippingDetailsSaved"
    />

    <ShippingDetailsModal
      v-if="showShippingModal"
      :show-shipping-details="true"
      :submit-emits-only="true"
      :order-item="pendingMovePayload?.order_item ?? order_item"
      :order="order"
      @close="handleShippingModalClose"
      @submit="handleShippingSubmit"
    />

    <Teleport to="body">
      <Transition name="fade">
        <ImagePreview
          v-if="showImagePreview"
          :activity_item="selectedActivityItem"
          :factory_product="selectedPreviewFactoryProduct"
          :order_no="order.order_no"
          @close="() => closePreviewModal()"
        />
      </Transition>

      <Transition name="fade">
        <UploadDesignSample
          :activity_items="activity_items"
          :modal-type="modal_type"
          :inValid="isMovingOrder || !validateSampleFiles()"
          v-if="isUploadDesignSample"
          :design_approval_activity_item_data="design_approval_activity_item_data"
          :design_approval_activity_navigation_index="design_approval_activity_navigation_index"
          @close="() => handleFilesModalClose()"
          @cancel="() => handleFilesModalClose()"
          @skip-sample-approval="skipSampleApproval"
          @navigate-sample-files="navigateSampleFiles"
          @submit-sample-files="submitSampleFiles"
          @change-sample-file="changeSampleFile"
          @delete-file="deleteFile"
          @close-message="closeMessage"
          @update-reason="updateReason"
        />
      </Transition>

      <Transition name="fade">
        <UploadProductionFilesModal
          v-if="showProductionFilesModal"
          :activity_items="activity_items"
          :activity_navigation_index="activity_navigation_index"
          :modal_type="modal_type"
          @close="() => handleFilesModalClose()"
          @cancel="() => handleFilesModalClose()"
          @skip-sample-approval="skipSampleApproval"
          @navigate-sample-files="navigateSampleFiles"
          @submit-sample-files="submitSampleFiles"
          @change-sample-file="changeSampleFile"
          @delete-file="deleteFile"
          @close-message="closeMessage"
          @update-reason="updateReason"
        />
      </Transition>
      <Transition name="fade">
        <ReviewSamplesModal
          v-if="showReviewSamplesModal"
          :activity_items="activity_items"
          :activity_navigation_index="design_approval_activity_navigation_index"
          :modal_type="modal_type"
          :order="order"
          @close="() => handleFilesModalClose()"
          @navigate-sample-files="navigateSampleFiles"
          @submit-sample-files="updateSamplesActivity"
        />
      </Transition>

      <Transition name="fade">
        <DeliveryNotes
          v-if="showDeliveryNotesModal"
          ref="deliveryNotesRef"
          :order_info="{
            order_item_id: order_item.id,
            order_no: order.order_no,
          }"
          @close="() => (showDeliveryNotesModal = false)"
          @save="handleDeliveryNotesSave"
        />
      </Transition>
      <Transition name="fade">
        <ViewShippedOrderJourney
          v-if="showShippedOrderModal"
          :tracking_response="order_item?.tracking_response"
          @close="showShippedOrderModal = false"
        />
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import CommentsList from '@/components/Dashboard/Order/components/CommentsList.vue';
import Select from '@/components/Shared/Select.vue';
import { formatDate, getTimeFromISO } from '@/composables';
import {
  activityStatus,
  can,
  CustimooOrderFlowStatuses,
  custom_product_path_info,
  findActivity,
  findActivityWithPosition,
  isNullOrEmpty,
  logError,
  mergeActivityArray,
  storage_url,
} from '@/composables';
import { showToast } from '@/composables/swal';
import { useOrderDetail } from '@/composables/useOrderDetail';
import { formatTimestampWithBrowserTimezone } from '@/composables/useTimeZoneFormatDate';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import {
  ActivityItem,
  ActivityItemPayload,
  ApprovalActivityFile,
  DashboardItem,
  DeliveryNote,
  DesignApprovalActivityItem,
  FactoryProduct,
  OrderDetail,
  OrderDetailItem,
  SkipCustomerApproval,
  StatusActivity,
} from '@/models';
import { DeliveryNotesResponse } from '@/models/DeliveryNotesResponse';
import { shippingDateChangeReasonText } from '@/utils/constants/shippingDateChangeReasons';
import { useMainStore } from '@/store';
import type { Comment } from '@/types/comments';
import { ArrowUpOnSquareIcon, BuildingStorefrontIcon } from '@heroicons/vue/24/outline';
import {
  ArrowUpRightIcon,
  CalendarIcon,
  CheckCircleIcon,
  CubeIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  FlagIcon,
  PencilIcon,
  PlusIcon,
  TruckIcon,
} from '@heroicons/vue/24/solid';
import { addDays } from 'date-fns';
import { storeToRefs } from 'pinia';

import { computed, ComputedRef, nextTick, onMounted, reactive, ref, watch } from 'vue';
import DeliveryNoteList from '../devilvery-notes/DeliveryNoteList.vue';
import DeliveryNotes from '../devilvery-notes/DeliveryNotes.vue';
import {
  generateDeliveryPdf,
  sortRowsByBox,
  generateBoxSeparatedPdfs,
} from '../devilvery-notes/usePDFexport';

import Tooltip from '@/components/Shared/Tooltip.vue';
import ShippingDetailsModal from '../Modals/ShippingDetailsModal.vue';
import type { ShippingDetailsFormData } from '../Modals/ShippingDetailsModal.vue';
import ImagePreview from './components/ImagePreview.vue';
import ReviewSamplesModal from './components/Modals/ReviewSamplesModal.vue';
import UploadProductionFilesModal from './components/Modals/UploadProductionFilesModal.vue';
import TimeLine from './components/TimeLine.vue';
import MoveOrderPicker from './components/MoveOrderPicker.vue';
import TimeLineItem from './components/TimeLineItem.vue';
import TimeLineItemContent from './components/TimeLineItemContent.vue';
import UploadDesignSample from './components/UploadDesignSample.vue';
import Accordion from '@/components/Shared/Accordion.vue';
import ViewShippedOrderJourney from './components/Modals/ViewShippedOrderJourney.vue';
import { STATUS_MAP } from '@/utils/constants/trackingResponseStatus';
import { makeReorderMessage as sharedMakeReorderMessage } from '@/utils/reorderMessage';
import { TrackingEvent } from '@/models/TrackingResponse';

interface Props {
  order: OrderDetail | null;
  order_item: OrderDetailItem;
  order_item_index: number;
  order_no: string;
  isLoading?: boolean;
  currentTab?: string;
  selectedRow?: DashboardItem | null;
}

const props = withDefaults(defineProps<Props>(), {
  order_item: null,
  order_no: '',
  order: null,
  isLoading: false,
});

const emit = defineEmits<{
  orderMoved: [responseData: any];
  orderUpdated: [orderItem: OrderDetailItem];
  commentsUpdated: [data: { activityId: number; comments: Comment[] }];
  onFetch: [];
  /** AB#21279 — the move-status form holds unsaved input, or no longer does. */
  dirtyChange: [dirty: boolean];
}>();

const STATUS_COLOR_MAP = {
  DELIVERED: 'text-primary',
  EXCEPTIONS: 'text-yellow-500',
  OUT_FOR_DELIVERY: 'text-blue-500',
  PICKED_UP: 'text-ink-soft',
  CLEARANCE_EVENT: 'text-purple-500',
  CUSTOMS_CLEARANCE: 'text-indigo-500',
};
const store = useMainStore();
const enterShippingDates = ref<Boolean>(false);
const editShippingLink = ref<Boolean>(false);
const enterShippingDetails = ref<Boolean>(false);
const est_shipping_date = ref<Date | string | null>(null);
const isPutInProduction = ref<Boolean>(false);
const showImagePreview = ref<Boolean>(false);
const isUploadDesignSample = ref<Boolean>(false);
const selectedActivityItem = ref<ActivityItem>(null);
// Reversible products (AB#19678): factory product backing the previewed
// activity item, so ImagePreview can render the inside-face pair.
const selectedPreviewFactoryProduct = ref<FactoryProduct>(null);
const design_approval_activity_item_data = ref<DesignApprovalActivityItem[]>([]);
const design_approval_activity_navigation_index = ref<number>(0);
const activity_item_data = ref<any[]>([]);
const modal_type = ref<string>();
const move_order_to_status = ref<string | { value: string; text: string } | null>(null);
const move_order_reason = ref<string>('');
// AB#21279 — a status picked or a reason typed is "unsaved input": the tab
// gets an orange dot and closing it asks first. Cleared by the move itself,
// which resets both fields.
watch(
  [move_order_to_status, move_order_reason],
  ([status, reason]) => emit('dirtyChange', Boolean(status) || reason.trim() !== ''),
  { immediate: true }
);
const isMovingOrder = ref<boolean>(false);
const showArtworkDesignModal = ref<boolean>(false);
const showSampleFilesModal = ref<boolean>(false);
const showProductionFilesModal = ref<boolean>(false);
const showReviewSamplesModal = ref<boolean>(false);
const artworkDesignItems = ref<any[]>([]);
const sampleFilesItems = ref<any[]>([]);
const productionFilesItems = ref<any[]>([]);
const activity_navigation_index = ref<number>(0);
const provideQuoteModal = ref(false);

const showDeliveryNotesModal = ref(false);
const deliveryNotesRef = ref<any>(null);
const isFetchingNotes = ref<boolean>(false);
const notesError = ref<string | null>(null);
const notes = ref<DeliveryNote[]>([]);
const remainingCount = ref<number>(0);
const orderedCount = ref<number>(0);
const shippedCount = ref<number>(0);
const isManualActivity = ref(false);
const showShippedOrderModal = ref(false);

/** Move-order flow: collect shipping in modal before API when status is shipped/completed and item has no tracking. */
const showShippingModal = ref(false);

interface PendingMoveOrderPayload {
  order_item: OrderDetailItem;
  order_item_index: number;
  selectedStatus: string;
  add_files: boolean;
  move_reason: string;
}

const pendingMovePayload = ref<PendingMoveOrderPayload | null>(null);

const { user: currentUser } = storeToRefs(store);

const storageUrl = computed(() => import.meta.env.VITE_APP_FILE_URL || '');

const getTrackingStatusColor = (status: string) => {
  if (!status) return '';

  const key = status.toUpperCase();
  return STATUS_COLOR_MAP[key] || 'text-ink-mute';
};
const getTrackingStatus = (status: string) => {
  if (!status) return '';

  const key = status.toUpperCase();
  return STATUS_MAP[key] || status;
};
const showSampleDesigns = (order_item_index: number, activity_item_index: number) => {
  // Open the design sample review modal context
  modal_type.value = 'test_sample';

  // Set order context
  activity_items.order_item_id = props.order_item?.id ?? null;
  activity_items.order_item_index = order_item_index;

  // Clone the selected status activity to avoid mutating source
  const activity_item = JSON.parse(
    JSON.stringify(props.order_item?.status_activities?.[activity_item_index] || {})
  );

  // Reset data containers
  activity_items.activity_item_data = [];
  design_approval_activity_item_data.value = [];

  // const username =
  //   currentUser.value?.name ||
  //   `${currentUser.value?.first_name || ''} ${currentUser.value?.last_name || ''}`.trim();
  // const user_id = currentUser.value?.id ?? null;

  // Build activity items payloads
  (activity_item?.activity_items || []).forEach((actItem: any) => {
    const actObj: any = {
      action: null,
      status: activity_item.status,
      message: '',
      files: [],
      factory_product_id: actItem.factory_product_id,
    };

    // Map files with storage URL and nocache param
    (actItem?.activity_files || []).forEach((actfile: any) => {
      const fileObj: any = {
        file: `${storageUrl.value}/${actfile.url}?nocache=${Math.random()}`,
        file_type: null,
      };
      actObj.files.push(fileObj);
    });
    if (isNullOrEmpty(actItem?.skip_customer_approval.reason)) {
      (activity_items.activity_item_data as Array<any>).push(actObj);
    }
  });

  // Initialize navigation indices if there is data
  if (
    Array.isArray(activity_items.activity_item_data) &&
    activity_items.activity_item_data.length > 0
  ) {
    activity_navigation_index.value = 0;
    design_approval_activity_navigation_index.value = 0;
  }
  showReviewSamplesModal.value = true;
};
const showArtworkDesignsAndReview = (
  order_item: OrderDetailItem,
  order_item_index: number,
  activity_item_index: number
) => {
  // Set modal type to factory_review
  modal_type.value = 'factory_review';

  // Set order context
  activity_items.order_item_id = order_item?.id ?? null;
  activity_items.order_item_index = order_item_index;

  // Clone the selected status activity to avoid mutating source
  const activity_item = JSON.parse(
    JSON.stringify(order_item?.status_activities?.[activity_item_index] || {})
  );

  // Reset data containers
  activity_items.activity_item_data = [];
  design_approval_activity_item_data.value = [];

  // Build activity items payloads
  (activity_item?.activity_items || []).forEach((actItem: any) => {
    const actObj: any = {
      action: null,
      status: activity_item.status,
      message: '',
      files: [],
      factory_product_id: actItem.factory_product_id,
    };

    // Map activity files if available
    if (actItem?.activity_files && actItem.activity_files.length > 0) {
      actItem.activity_files.forEach((actfile: any) => {
        if (actfile.url) {
          const fileObj: any = {
            file: `${storageUrl.value}/${actfile.url}?nocache=${Math.random()}`,
            file_type: null,
          };
          actObj.files.push(fileObj);
        }
      });
    } else {
      // Fallback to product front/back images if no activity files
      const factory_product = order_item.factory_products.find(
        (fp) => fp.id === actItem.factory_product_id
      );

      if (factory_product?.front_image) {
        const front_fileObj: any = {
          file: `${storageUrl.value}/${factory_product.front_image}?nocache=${Math.random()}`,
          file_type: null,
        };
        actObj.files.push(front_fileObj);
      }

      if (factory_product?.back_image) {
        const back_fileObj: any = {
          file: `${storageUrl.value}/${factory_product.back_image}?nocache=${Math.random()}`,
          file_type: null,
        };
        actObj.files.push(back_fileObj);
      }
    }

    // Setup skip customer approval
    const factory_product = order_item.factory_products.find(
      (fp) => fp.id === actItem.factory_product_id
    );

    let skipCustomerApproval = {
      design_customer_approval: true,
      reason: null,
      show_reason_modal: false,
      username: currentUser.value?.name,
      user_id: currentUser.value?.id,
      role_name: currentUser.value?.userroles?.[0]?.name,
    };

    if (factory_product?.sku?.design_customer_approval !== undefined) {
      skipCustomerApproval.design_customer_approval =
        !!factory_product.sku.design_customer_approval;
    }

    actObj.skip_customer_approval = skipCustomerApproval;
    (activity_items.activity_item_data as Array<any>).push(actObj);

    // Add to design approval data if needed
    if (factory_product?.sku?.design_customer_approval !== undefined) {
      if (actObj.skip_customer_approval.design_customer_approval) {
        design_approval_activity_item_data.value.push(actObj);
      }
    } else {
      design_approval_activity_item_data.value.push(actObj);
    }
  });

  // Initialize navigation indices if there is data
  if (
    Array.isArray(activity_items.activity_item_data) &&
    activity_items.activity_item_data.length > 0
  ) {
    activity_navigation_index.value = 0;
    design_approval_activity_navigation_index.value = 0;
  }

  // Open the review samples modal
  showReviewSamplesModal.value = true;
};

const canAddComments = computed(() => can('add-comment'));
const canEditComments = computed(() => can('edit-comment'));
const canDeleteComments = computed(() => can('delete-comment'));
const canResolveComments = computed(() => {
  return (
    currentUser.value?.userroles?.some((role) =>
      ['Admin', 'SuperAdmin', 'OrderAdministrator'].includes(role.name)
    ) || false
  );
});

const handleCommentsUpdated = (activity: any, comments: Comment[]) => {
  activity.comments = comments;
  emit('commentsUpdated', {
    activityId: activity.id,
    comments: comments,
  });
};

const allActivitiesCount = computed(() => {
  return props.order_item?.status_activities && props.order_item?.status_activities.length > 0
    ? props.order_item?.status_activities.length
    : 0;
});
const shipmentStatus: ComputedRef<string | null> = computed(() => {
  if (remainingCount.value === 0 && orderedCount.value > 0) {
    return 'Shipped';
  } else if (shippedCount.value > 0 && shippedCount.value < orderedCount.value) {
    return 'Partially Shipped';
  } else {
    return null;
  }
});

const activity_items = reactive<ActivityItemPayload>({
  order_move: 0,
  order_item_id: null,
  order_item_index: null,
  order_item_status: null,
  tracking_link: null,
  tracking_no: '',
  shipping_company: null,
  reason: null,
  skip_saving_shipping_details: false,
  activity_item_data: null,
  mawb_number: null,
  mawb_airline: null,
});

const allowCreateDeliveryNote: ComputedRef<boolean> = computed(() => {
  if (remainingCount.value === 0 && notes.value.length === 0) {
    return can('create-delivery-notes') && !!props.order_item.id;
  }

  if (remainingCount.value > 0) {
    return can('create-delivery-notes') && !!props.order_item.id;
  }
  return false;
});
const shouldShowShippingModal = computed(() => {
  const show =
    enterShippingDetails.value ||
    enterShippingDates.value ||
    isPutInProduction.value ||
    editShippingLink.value;
  return show;
});
const openTrackingLinkModel = () => {
  editShippingLink.value = true;
};

const handleFilesModalClose = () => {
  showProductionFilesModal.value = false;
  activity_navigation_index.value = 0;
  showSampleFilesModal.value = false;
  isUploadDesignSample.value = false;
  showReviewSamplesModal.value = false;
  design_approval_activity_navigation_index.value = 0;
};

const { isQuoteOrder, showQuoteButtons, showProvideQuoteButton, rejectQuote, provideQuotePrice } =
  useOrderDetail();

const order: ComputedRef<OrderDetail> = computed(() => props.order);

const {
  FACTORYREVIEW,
  ORDERAPPROVE,
  ORDERCANCEL,
  FACTORYAPPROVED,
  CUSTOMERREVIEW,
  CUSTOMERAPPROVED,
  CUSTOMERREJECTED,
  ORDERINPRODUCTION,
  ORDERSHIPPED,
  ORDERCOMPLETED,
  FACTORY_APPROVE_REJECT,
  PRODUCTIONFILESUPLOADED,
  QUALITYCONTROL,
  QUOTEPROVIDED,
  FACTORYREJECTED,
} = CustimooOrderFlowStatuses;

const moveOrderOptions = ref([
  { value: FACTORYREVIEW, text: 'Order Created' },
  { value: ORDERAPPROVE, text: 'Mark to factory' },
  { value: ORDERCANCEL, text: 'Order Cancelled' },
  { value: FACTORYAPPROVED, text: 'Artwork Approval' },
  { value: FACTORY_APPROVE_REJECT, text: 'Artwork Approval/Artwork Rejected' },
  { value: CUSTOMERREVIEW, text: 'Customer Review' },
  { value: ORDERINPRODUCTION, text: 'In Production' },
  { value: ORDERSHIPPED, text: 'Order Shipped' },
  { value: ORDERCOMPLETED, text: 'Order Completed' },
]);

/**
 * Status options for ONE order item's dropdown (#20530 / Zendesk #7583).
 *
 * "Pushed to QC" replaced the old behaviour where QC began automatically the
 * moment an item entered production — that closed "In Production" instantly and
 * made QC look like it spanned the whole production run. The order admin now
 * pushes it when the goods are genuinely ready for inspection.
 *
 * Gated per ITEM rather than per order: each item has its own dropdown and Move
 * button, and orders split per factory, so one item can sit at a Qarma factory
 * while another does not. Offering it on a non-Qarma item would be a control the
 * backend silently ignores. Inserted after "In Production" so the list reads in
 * workflow order.
 */
const moveOptionsFor = (order_item: OrderDetailItem | null | undefined) => {
  if (!order_item?.is_qarma_enabled) return moveOrderOptions.value;

  const options = [...moveOrderOptions.value];
  const afterInProduction = options.findIndex((o) => o.value === ORDERINPRODUCTION) + 1;
  options.splice(afterInProduction, 0, { value: QUALITYCONTROL, text: 'Pushed to QC' });
  return options;
};

/** Skip-saving tracking is disallowed when fulfillment / store sync depends on it. */
const orderRequiresShippingPersist = (): boolean => {
  const o = props.order;
  console.log('company platform', o?.company?.platform);
  if (!o) return false;
  if (String(o.company?.platform ?? '').toLowerCase() === 'shopify') return true;
  const id = o.ecommerce_order_id;
  if (id == null || id === '') return false;
  return String(id).trim().length > 0;
};

const currentInProductionOrQCIndex = computed(() => {
  if (!props.order_item?.status_activities) return -1;
  for (let i = 0; i < props.order_item?.status_activities.length; i++) {
    const status = props.order_item?.status_activities[i].status;
    if (status === ORDERINPRODUCTION || status === QUALITYCONTROL) {
      return i;
    }
  }
  return -1;
});
const filteredTrackingHistory = computed(() => {
  if (!props.order_item?.tracking_response) return null;

  const latestDate = props.order_item.tracking_response.latest_event?.date;

  return [...props.order_item.tracking_response.history]
    .filter((h) => h.date !== latestDate)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
});
const latestEvent = computed(() => {
  const event = props.order_item?.tracking_response?.latest_event as TrackingEvent | undefined;
  return {
    date: event.date || 'N/A',
    city: event.city || 'N/A',
    country_name: event.country_name || 'N/A',
    derived_status: event.derived_status || 'Unknown',
  };
});

const handleRejectQuote = async () => {
  if (props.order) {
    try {
      await rejectQuote(props.order);
      emit('orderMoved', { success: true, action: 'quote_rejected' });
    } catch (error) {
      console.error('Error rejecting quote:', error);
    }
  }
};

const handleProvideQuotePrice = () => {
  if (props.order) {
    try {
      provideQuotePrice(props.order);
      emit('orderMoved', { success: true, action: 'quote_provided' });
    } catch (error) {
      console.error('Error providing quote:', error);
    }
  }
};

const getActivityContent = (activity_items: ActivityItem[], activity_status: string) => {
  let content_group = [];
  activity_items?.forEach((item, index) => {
    const images =
      item.activity_files?.map((file) => {
        return {
          url: file.url,
          alt: 'product img',
          ext: getFileExtension(file.url),
        };
      }) ?? [];

    // Reversible products (AB#19678): older activity snapshots only carry the
    // outside pair. When the activity files are the product's own design
    // previews (URL match), append the inside pair from the factory_products
    // snapshot; newer snapshots already include them, so dedup by URL.
    const activityFactoryProduct =
      props.order_item?.factory_products?.find((fp) => fp.id === item.factory_product_id) ??
      props.order_item?.factory_products?.[index];
    if (activityFactoryProduct?.inside_front_image || activityFactoryProduct?.inside_back_image) {
      const imageUrls = new Set(images.map((image) => image.url));
      const isDesignPreviewPair =
        imageUrls.has(activityFactoryProduct.front_image) ||
        imageUrls.has(activityFactoryProduct.back_image);
      if (isDesignPreviewPair) {
        for (const insideUrl of [
          activityFactoryProduct.inside_front_image,
          activityFactoryProduct.inside_back_image,
        ]) {
          if (insideUrl && !imageUrls.has(insideUrl)) {
            images.push({ url: insideUrl, alt: 'product img', ext: getFileExtension(insideUrl) });
          }
        }
      }
    }

    content_group.push({
      images,
      design_id: props.order_item.factory_products[index]
        ? props.order_item.factory_products[index].design_id
        : null,
      design_name: props.order_item?.factory_products[index]?.design_name ?? null,
      is_manual_order: props.order?.additional_fields?.is_manual_order === true,
      nickName: props.order_item?.factory_products[index]?.design_nick_name ?? null,
      addons:
        props.order_item?.factory_products[index]?.addons?.length > 0
          ? props.order_item?.factory_products[index]?.addons
          : [],
      skipReason:
        item.skip_customer_approval && item.skip_customer_approval.reason
          ? item.skip_customer_approval.reason
          : null,
      message: item.message ?? null,
      status: item.status,
      reorder_message: makeReorderMessage(
        item.factory_product_id,
        props.order_item?.factory_products
      ),
      third_party_approval_obj: item.third_party_approval_obj,
      quality_control: item.quality_control,
    });
  });
  return { content_group, activity_status, general_comments: props.order.general_comments ?? null };
};

const getActivityIcon = (status) => {
  const iconMap = {
    [FACTORYREVIEW]: PencilIcon,
    [ORDERAPPROVE]: DocumentTextIcon,
    [ORDERCANCEL]: ExclamationCircleIcon,
    [FACTORYAPPROVED]: CheckCircleIcon,
    [CUSTOMERREVIEW]: PencilIcon,
    [CUSTOMERAPPROVED]: CheckCircleIcon,
    [CUSTOMERREJECTED]: ExclamationCircleIcon,
    [ORDERINPRODUCTION]: CubeIcon,
    [ORDERSHIPPED]: TruckIcon,
    [ORDERCOMPLETED]: FlagIcon,
    [PRODUCTIONFILESUPLOADED]: DocumentTextIcon,
    [QUALITYCONTROL]: CheckCircleIcon,
    [QUOTEPROVIDED]: DocumentTextIcon,
  };
  return iconMap[status] || DocumentTextIcon;
};

const getCompletedIcon = (status) => {
  return CheckCircleIcon;
};

const skipSampleApproval = () => {
  design_approval_activity_item_data.value[
    design_approval_activity_navigation_index.value
  ].skip_customer_approval.show_reason_modal = true;
};
const closeMessage = (design_activity_item, design_activity_index) => {
  design_approval_activity_item_data.value[
    design_approval_activity_navigation_index.value
  ].skip_customer_approval.show_reason_modal = false;
  design_approval_activity_item_data.value[
    design_approval_activity_navigation_index.value
  ].skip_customer_approval.reason = null;
  design_approval_activity_item_data.value[
    design_approval_activity_navigation_index.value
  ].skip_customer_approval.design_customer_approval = true;
};

const updateReason = () => {
  if (design_approval_activity_item_data.value.length) {
    if (
      design_approval_activity_item_data.value.length - 1 !==
      design_approval_activity_navigation_index.value
    ) {
      if (
        design_approval_activity_item_data.value[design_approval_activity_navigation_index.value]
          .skip_customer_approval.show_reason_modal &&
        design_approval_activity_item_data.value[design_approval_activity_navigation_index.value]
          .skip_customer_approval.reason !== null &&
        design_approval_activity_item_data.value[design_approval_activity_navigation_index.value]
          .skip_customer_approval.reason !== ''
      ) {
        design_approval_activity_item_data.value[
          design_approval_activity_navigation_index.value
        ].skip_customer_approval.design_customer_approval = false;
        design_approval_activity_item_data.value[
          design_approval_activity_navigation_index.value
        ].skip_customer_approval.show_reason_modal = false;
        navigateSampleFiles('next', true);
      } else {
        showToast('Please provide a valid reason to skip', 'warning');
      }
    } else {
      design_approval_activity_item_data.value[
        design_approval_activity_navigation_index.value
      ].skip_customer_approval.design_customer_approval = false;
      design_approval_activity_item_data.value[
        design_approval_activity_navigation_index.value
      ].skip_customer_approval.show_reason_modal = false;
      submitSampleFiles(true);
    }
  }
};

const navigateSampleFiles = (
  direction: 'next' | 'back',
  skip: boolean = false,
  sampleApproval: boolean = false
) => {
  if (direction === 'next') {
    if (!skip && validateSampleFiles()) {
      if (modal_type.value === 'test_sample' && !sampleApproval) {
        let limit = design_approval_activity_item_data.value.length;
        if (design_approval_activity_navigation_index.value + 1 < limit) {
          design_approval_activity_navigation_index.value =
            design_approval_activity_navigation_index.value + 1;
        }
      } else {
        let limit = Array.isArray(activity_items.activity_item_data)
          ? activity_items.activity_item_data.length
          : 0;
        if (activity_navigation_index.value + 1 < limit && !sampleApproval) {
          activity_navigation_index.value = design_approval_activity_navigation_index.value + 1;
        } else {
          if (design_approval_activity_navigation_index.value + 1 < limit) {
            design_approval_activity_navigation_index.value =
              design_approval_activity_navigation_index.value + 1;
          }
        }
      }
    } else if (skip) {
      if (modal_type.value === 'test_sample') {
        let limit = design_approval_activity_item_data.value.length;
        if (design_approval_activity_navigation_index.value + 1 < limit) {
          design_approval_activity_navigation_index.value =
            design_approval_activity_navigation_index.value + 1;
        }
      } else {
        let limit = Array.isArray(activity_items.activity_item_data)
          ? activity_items.activity_item_data.length
          : 0;
        if (activity_navigation_index.value + 1 < limit) {
          activity_navigation_index.value = design_approval_activity_navigation_index.value + 1;
        }
      }
    } else {
      showToast('Please upload front/back sample file', 'warning');
    }
  } else {
    if (modal_type.value === 'test_sample' && !sampleApproval) {
      if (design_approval_activity_navigation_index.value - 1 >= 0) {
        design_approval_activity_navigation_index.value--;
      }
    } else {
      if (activity_navigation_index.value - 1 >= 0 && !sampleApproval) {
        activity_navigation_index.value--;
      } else {
        if (design_approval_activity_navigation_index.value - 1 >= 0) {
          design_approval_activity_navigation_index.value--;
        }
      }
    }
  }
};
const validateSampleFiles = () => {
  let valid = true;
  if (modal_type.value === 'test_sample') {
    if (design_approval_activity_item_data.value.length > 0) {
      if (
        design_approval_activity_item_data.value[design_approval_activity_navigation_index.value]
          .files
      ) {
        design_approval_activity_item_data.value[
          design_approval_activity_navigation_index.value
        ].files.forEach((obj) => {
          if (obj.file === null) valid = false;
        });
      } else {
        valid = false;
      }
    }
  } else {
    if (
      Array.isArray(activity_items.activity_item_data) &&
      activity_items.activity_item_data.length > 0
    ) {
      if (
        activity_items.activity_item_data[activity_navigation_index.value].files &&
        activity_items.activity_item_data[activity_navigation_index.value].files.length > 0
      )
        activity_items.activity_item_data[activity_navigation_index.value].files.forEach((obj) => {
          if (obj.file === null) valid = false;
        });
    } else {
      valid = false;
    }
  }
  return valid;
};

const deleteFile = (act_item_index, file_index) => {
  if (modal_type.value === 'test_sample') {
    design_approval_activity_item_data.value[act_item_index].files[file_index].file = null;
  } else {
    activity_items.activity_item_data[act_item_index].files[file_index].file = null;
  }
};

const handleDeliveryNotesSave = async () => {
  await fetchNotes();
  showDeliveryNotesModal.value = false;
};
const changeSampleFile = (files, act_item_index, file_index) => {
  if (modal_type.value === 'test_sample') {
    design_approval_activity_item_data.value[act_item_index].files[file_index].file = files[0];
  } else {
    activity_items.activity_item_data[act_item_index].files[file_index].file = files[0];
  }
};
const submitSampleFiles = (skip = false) => {
  if (skip || validateSampleFiles()) {
    submitActivity('form_data');
  } else {
    showToast('Please upload front/back sample file', 'warning');
  }
};
const updateSamplesActivity = () => {
  if (Array.isArray(activity_items.activity_item_data)) {
    const finalApprovalData = activity_items.activity_item_data.map((act_item, index) => {
      return {
        action: act_item.action,
        status:
          modal_type.value === 'test_sample'
            ? act_item.action === 'reject'
              ? CUSTOMERREJECTED
              : CUSTOMERAPPROVED
            : act_item.action === 'reject'
              ? FACTORYREJECTED
              : FACTORYAPPROVED,
        message: act_item.message,
        files: act_item.files,
        factory_product_id: act_item.factory_product_id,
        manual_action: true,
        skip_customer_approval: {
          design_customer_approval: true,
          reason: null,
          show_reason_modal: false,
          username: currentUser.value.name,
          user_id: currentUser.value.id,
          role_name: currentUser.value.roles[0].name,
        },
      };
    });
    activity_items.activity_item_data = finalApprovalData;
    showReviewSamplesModal.value = false;
    if (modal_type.value === 'test_sample') {
      submitActivity('form_data', true);
    } else {
      submitActivity('form_data', false);
    }
  }
};

const resetActivity = async () => {
  modal_type.value = null;
  isUploadDesignSample.value = false;
  showProductionFilesModal.value = false;
  showReviewSamplesModal.value = false;
  resetActivityData();
};
const resetActivityData = () => {
  activity_items.order_move = 0;
  activity_items.order_item_id = null;
  activity_items.order_item_index = null;
  ((activity_items.tracking_link = null),
    (activity_items.tracking_no = ''),
    (activity_items.shipping_company = null),
    (activity_items.activity_item_data = []));
  activity_items.skip_saving_shipping_details = false;
  activity_navigation_index.value = null;
  activity_items.order_item_status = null;
  activity_items.reason = null;
  activity_item_data.value = [];
  activity_items.mawb_number = null;
  activity_items.mawb_airline = null;
};

const getFileExtension = (url) => {
  if (!url) return '';
  return url.split('.').pop().toLowerCase();
};

const evaluateClass = (order_item, status) => {
  if (order_item?.activity_files && order_item?.activity_files.length > 0) {
    if (
      order_item?.skip_customer_approval &&
      status !== ORDERINPRODUCTION &&
      (order_item?.skip_customer_approval?.design_customer_approval === 'false' ||
        order_item?.skip_customer_approval?.design_customer_approval === false)
    ) {
      return 'submit-block skipped';
    } else {
      return 'submit-block';
    }
  } else {
    if (
      order_item?.skip_customer_approval &&
      status !== ORDERINPRODUCTION &&
      (order_item?.skip_customer_approval?.design_customer_approval === 'false' ||
        order_item?.skip_customer_approval?.design_customer_approval === false)
    ) {
      return 'skipped';
    } else {
      return '';
    }
  }
};

const showPreview = (activity_item: ActivityItem) => {
  showImagePreview.value = true;
  selectedActivityItem.value = activity_item;
  // Reversible products (AB#19678): hand the modal the factory product so it
  // can show the inside pair even when the activity snapshot lacks them.
  selectedPreviewFactoryProduct.value =
    props.order_item?.factory_products?.find(
      (factory_product) => factory_product.id === activity_item?.factory_product_id
    ) ?? null;
};

const closePreviewModal = () => {
  showImagePreview.value = false;
  selectedActivityItem.value = null;
  selectedPreviewFactoryProduct.value = null;
};
const downloadActivityProductionFile = (files) => {
  console.log('Download files:', files);
};

// AB#20682: delegates to the shared helper. This was the second of two copies
// in this repo and the one carrying the inverted "(order not confirmed)" check
// (it tested order.value.order_no — the CURRENT order — instead of the
// referenced order's order_number).
const makeReorderMessage = (factory_product_id, factory_products) => {
  let factory_product = factory_products.find((item) => item.id === factory_product_id);
  let message = sharedMakeReorderMessage(factory_product);

  return message;
};

const uploadSampleFiles = (
  order_item: OrderDetailItem,
  activity_index: number,
  activity_items_arg: StatusActivity
) => {
  activity_items_arg.order_item_id = order_item.id;
  activity_item_data.value = [];
  design_approval_activity_item_data.value = [];
  let activity_item = Object.assign({}, order_item.status_activities[activity_index]);
  let actItem = order_item.status_activities.find(
    (status_activity) => status_activity.status === CUSTOMERREVIEW
  );

  for (let factProd of order_item.factory_products) {
    if (factProd.status !== CUSTOMERAPPROVED) {
      let product_images;
      if (factProd.front_image) {
        product_images = [factProd.front_image];
      }
      if (factProd.back_image) {
        product_images.push(factProd.back_image);
      }
      if (!factProd.front_image && !factProd.back_image) {
        product_images = [
          `${storage_url}/${custom_product_path_info.url}`,
          `${storage_url}/${custom_product_path_info.url}`,
        ];
      }
      let actObj: DesignApprovalActivityItem = {
        action: null,
        factory_product_id: null,
        files: [],
        message: null,
        product_files: [],
        rejected_files: [],
        rejected_msg: null,
        skip_customer_approval: {
          design_customer_approval: false,
          reason: null,
          role_name: null,
          show_reason_modal: false,
          user_id: null,
          username: null,
        },
        status: ORDERCOMPLETED,
      };
      actObj.action = null;
      actObj.status = CUSTOMERREVIEW;
      actObj.message = '';
      actObj.files = [];
      actObj.factory_product_id = factProd.id;
      actObj.product_files = product_images;
      actObj.rejected_files = [];
      actObj.rejected_msg = '';

      if (activity_item.status === CUSTOMERREJECTED) {
        activity_item.activity_items.forEach((fileObj) => {
          if (fileObj.factory_product_id === factProd.id) {
            actObj.rejected_files = [fileObj.activity_files[0].url, fileObj.activity_files[1].url];
            actObj.rejected_msg = fileObj.message;
          }
        });
      }

      let skipCustomerApproval: SkipCustomerApproval = {
        design_customer_approval: true,
        reason: null,
        show_reason_modal: false,
        username: store.user?.name,
        user_id: store.user?.id,
        role_name: store.user?.roles[0]?.name,
      };
      if (activity_item.status === CUSTOMERREJECTED) {
        let all_activity_Items = null;
        let status_to_check = FACTORYREVIEW;
        if (order.value && order.value.ecommerce_order_id) {
          status_to_check = ORDERAPPROVE;
        }

        let artwork_created_activity = findActivityWithPosition(
          order_item?.status_activities,
          status_to_check,
          0
        );
        let submitted_customer_review_activity = findActivity(
          order_item?.status_activities,
          CUSTOMERREVIEW,
          artwork_created_activity?.activity_items.length
        );

        if (submitted_customer_review_activity) {
          all_activity_Items = mergeActivityArray(
            activity_item.activity_items,
            artwork_created_activity.activity_items,
            submitted_customer_review_activity
          );
        }

        let activityItem = all_activity_Items?.find((activity_item) => {
          return activity_item.factory_product_id === factProd.id;
        });

        if (activityItem) {
          skipCustomerApproval.design_customer_approval =
            activityItem?.skip_customer_approval?.design_customer_approval === 'true' ||
            activityItem?.skip_customer_approval?.design_customer_approval === true ||
            activityItem?.skip_customer_approval?.design_customer_approval === 1
              ? true
              : false;
          skipCustomerApproval.reason = null;
          skipCustomerApproval.show_reason_modal = false;
          skipCustomerApproval.username = store.user?.name;
          skipCustomerApproval.user_id = store.user?.id;
          skipCustomerApproval.role_name = store.user?.roles[0]?.name;
          if (activity_item.status === CUSTOMERREJECTED) {
            actItem.activity_items.forEach((fileObj) => {
              if (fileObj.factory_product_id === factProd.id) {
                actObj.rejected_files = [
                  fileObj.activity_files[0].url,
                  fileObj.activity_files[1].url,
                ];
              }
            });
          }
        }
      } else {
        if (factProd?.sku?.design_customer_approval !== undefined) {
          skipCustomerApproval.design_customer_approval = !!factProd.sku.design_customer_approval;
          skipCustomerApproval.reason = null;
          skipCustomerApproval.show_reason_modal = false;
          skipCustomerApproval.username = store.user?.name;
          skipCustomerApproval.user_id = store.user?.id;
          skipCustomerApproval.role_name = store.user?.roles[0]?.name;
        }
      }
      actObj.skip_customer_approval = skipCustomerApproval;
      let fileObj: ApprovalActivityFile = {
        file: null,
        file_type: 'object',
        side: 'front',
      };
      actObj.files.push(fileObj);

      let fileObj2: ApprovalActivityFile = { ...fileObj };
      fileObj2.side = 'back';
      actObj.files.push(fileObj2);

      activity_item_data.value.push(actObj);
      if (factProd.sku?.design_customer_approval !== undefined) {
        if (
          (actObj && actObj?.skip_customer_approval?.design_customer_approval === true) ||
          actObj?.skip_customer_approval?.design_customer_approval === true
        ) {
          design_approval_activity_item_data.value.push(actObj);
        }
      } else {
        design_approval_activity_item_data.value.push(actObj);
      }
    }
  }
  modal_type.value = 'test_sample';
  isUploadDesignSample.value = true;

  activity_items.order_item_id = order_item.id;
  activity_items.order_item_index = activity_index;

  sampleFilesItems.value = [];

  for (const factProd of order_item.factory_products) {
    if (factProd.status !== CUSTOMERAPPROVED) {
      const actObj = {
        action: null,
        status: CUSTOMERREVIEW,
        message: '',
        files: [],
        factory_product_id: factProd.id,
        product_files: [factProd.front_image, factProd.back_image],
        rejected_files: [],
        rejected_msg: '',
        skip_customer_approval: {
          design_customer_approval: true,
          reason: null,
          show_reason_modal: false,
          username: JSON.parse(localStorage.getItem('user') || '{}')?.name,
          user_id: JSON.parse(localStorage.getItem('user') || '{}')?.id,
          role_name: JSON.parse(localStorage.getItem('user') || '{}')?.role?.name,
        },
      };

      if (factProd?.sku?.design_customer_approval !== undefined) {
        actObj.skip_customer_approval.design_customer_approval =
          !!factProd.sku.design_customer_approval;
      }

      const frontFile = {
        file: null,
        file_type: 'object',
        side: 'front',
      };
      actObj.files.push(frontFile);

      const backFile = {
        file: null,
        file_type: 'object',
        side: 'back',
      };
      actObj.files.push(backFile);

      sampleFilesItems.value.push(actObj);
    }
  }

  showSampleFilesModal.value = true;
};

const putOrderInProduction = async (status, order_item, order, notify, is_production) => {
  try {
    await makeActivityData(
      order_item,
      props.order_item_index,
      ORDERINPRODUCTION,
      false,
      move_order_reason.value,
      false
    );

    const defaultDate = addDays(new Date(), 21);
    est_shipping_date.value = defaultDate;

    isPutInProduction.value = false;
  } catch (error) {
    console.error('Error putting order in production:', error);
  }
};

const makeActivityData = (
  order_item,
  order_item_index,
  status,
  add_files,
  move_order_reason = null,
  manual_action = false,
  submitForm: boolean = true
) => {
  if (status === ORDERSHIPPED && activity_items.order_move === 0) {
    if (orderRequiresShippingPersist()) {
      activity_items.skip_saving_shipping_details = false;
    }
    if (!activity_items.skip_saving_shipping_details) {
      if (!activity_items.shipping_company) {
        showToast('Please select shipping company', 'warning');
        return false;
      }
      if (!activity_items.tracking_no) {
        showToast('Please provide tracking no.', 'warning');
        return false;
      }
      if (!activity_items.tracking_link || String(activity_items.tracking_link).trim() === '') {
        showToast('Please provide tracking link', 'warning');
        return false;
      }
    }
  }

  activity_items.order_item_id = order_item.id;
  activity_items.order_item_index = order_item_index;
  activity_items.order_item_status = status;
  activity_navigation_index.value = 0;

  activity_items.activity_item_data = [];

  for (let factProd of order_item.factory_products) {
    let actObj = {
      action: null,
      status: status,
      reason: move_order_reason,
      manual_action: manual_action,
      message: '',
      files: [],
      factory_product_id: factProd.id,
    };

    if (add_files) {
      let front_file = {
        file: factProd ? factProd.front_image : null,
        file_type: '',
        side: 'front',
      };
      actObj.files.push(front_file);

      let back_file = {
        file: factProd ? factProd.back_image : null,
        file_type: '',
        side: 'back',
      };
      actObj.files.push(back_file);
    }

    activity_items.activity_item_data.push(actObj);
  }
  if (submitForm) {
    submitActivity('form_data');
  }
};

const showArtworkDesigns = (order_item, index, activity_index) => {
  activity_items.order_item_id = order_item.id;
  activity_items.order_item_index = index;

  const activity_item = { ...order_item.status_activities[activity_index] };

  artworkDesignItems.value = [];

  for (const actItem of activity_item.activity_items) {
    const actObj = {
      action: null,
      status: activity_item.status,
      message: null,
      files: [],
      factory_product_id: actItem.factory_product_id,
      skip_customer_approval: {
        design_customer_approval: true,
        reason: null,
        show_reason_modal: false,
        username: JSON.parse(localStorage.getItem('user') || '{}')?.name,
        user_id: JSON.parse(localStorage.getItem('user') || '{}')?.id,
        role_name: JSON.parse(localStorage.getItem('user') || '{}')?.role?.name,
      },
    };

    for (const actfile of actItem.activity_files) {
      if (actfile.url) {
        const fileObj = {
          file: actfile.url,
          file_type: null,
          side: actfile.side || 'front',
        };
        actObj.files.push(fileObj);
      }
    }

    const factory_product = order_item.factory_products.find(
      (factory_product) => factory_product.id === actItem.factory_product_id
    );

    if (factory_product?.sku?.design_customer_approval !== undefined) {
      actObj.skip_customer_approval.design_customer_approval =
        !!factory_product.sku.design_customer_approval;
    }

    artworkDesignItems.value.push(actObj);
  }

  showArtworkDesignModal.value = true;
};

const getActivityItemIndex = (current_activity_items: ActivityItem[], image_url: string) => {
  return current_activity_items.findIndex((item) => {
    if (item.activity_files.find((file) => file.url === image_url)) {
      return true;
    }
    // Reversible products (AB#19678): inside previews rendered on the timeline
    // are appended from the factory_products snapshot and may not be part of
    // the activity_files of older activity snapshots.
    const fp = props.order_item?.factory_products?.find(
      (factory_product) => factory_product.id === item.factory_product_id
    );
    return !!fp && (fp.inside_front_image === image_url || fp.inside_back_image === image_url);
  });
};

const reversedEstShippingDates = (order_item: any) => {
  return order_item.est_shipping_date ? [...order_item.est_shipping_date].reverse() : [];
};

const normalizeShipDateKey = (value: unknown): string => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.slice(0, 10);
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  return String(value).slice(0, 10);
};

const shippingDateChangeReasonFor = (order_item: any, date: string): string => {
  const log = order_item?.shipping_date_change_log;
  if (!Array.isArray(log) || !date) {
    return '';
  }
  const key = normalizeShipDateKey(date);
  let reason = '';
  for (const entry of log) {
    if (entry && normalizeShipDateKey(entry.date) === key) {
      // AB#21519: renders the reason code's label, or the custom text the admin wrote.
      // Entries predating the reason-code list fall back to their hand-typed prose.
      reason = shippingDateChangeReasonText(entry);
    }
  }
  return reason;
};

const handleShippingDateSaved = (date) => {
  emit('orderMoved', { success: true, action: 'shipping_date_saved', date });
};

const handlePutInProductionSaved = (notify) => {
  putOrderInProduction(ORDERINPRODUCTION, props.order_item, props.order, notify, true);
  emit('orderMoved', {
    success: true,
    action: 'put_in_production',
    notify,
  });
};
const getAttributionHtml = (status_activity: StatusActivity): string => {
  let message = '';

  if (status_activity.action_log && status_activity.action_log.custom_message) {
    message = status_activity.message;
  } else {
    message = activityStatus[status_activity.status]?.message || '';
  }
  let html = `<div class="activity-text p-2 text-sm text-ink-mute">`;

  if (status_activity.status === ORDERINPRODUCTION || status_activity.status === CUSTOMERREVIEW) {
    html += message;
  } else {
    html += message;
    if (status_activity.status === QUALITYCONTROL && status_activity.quality_control_status) {
      let badgeClass = '';
      switch (status_activity.quality_control_status) {
        case 'Approved':
          badgeClass = 'bg-green-100 text-green-800';
          break;
        case 'Rejected':
          badgeClass = 'bg-red-100 text-red-800';
          break;
        case 'Pending':
          badgeClass = 'bg-yellow-100 text-yellow-800';
          break;
      }
      html += `
        <div class="mt-1">
          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}">
            ${status_activity.quality_control_status}
          </span>
        </div>
      `;
    }
  }
  html += `</div>`;
  return html;
};

const fetchEstimatedShippingDate = async (order_item_id) => {
  isManualActivity.value = true;
  try {
    const response = await http.post(`/order/get-shipping-date/${order_item_id}`);
    if (response.data && response.data.estimated_shipping_date) {
      est_shipping_date.value = response.data.estimated_shipping_date;
    }
  } catch (error) {
    est_shipping_date.value = '';
  }
};

const handlePutInProduction = async (order_item) => {
  await fetchEstimatedShippingDate(order_item.id);
  isPutInProduction.value = true;
};

const handleEnterShippingDate = async (order_item) => {
  await fetchEstimatedShippingDate(order_item.id);
  enterShippingDates.value = true;
};

onMounted(async () => {
  if (can('upload-production-files-activity')) {
    moveOrderOptions.value.push({
      value: PRODUCTIONFILESUPLOADED,
      text: 'Upload production files',
    });
  }
  if (can('list-delivery-notes')) {
    await fetchNotes();
  }
});
const handleShippingDetailsSaved = async (details) => {
  if (editShippingLink.value) {
    if (details.skip_saving_shipping_details) {
      editShippingLink.value = false;
      enterShippingDetails.value = false;
      emit('orderMoved', {
        success: true,
        action: 'shipping_details_save_skipped',
        details,
      });
      return;
    }
    try {
      const payload = {
        order_item_id: props.order_item.id,
        ...details,
        mawb_number: details.mawb_number ?? null,
        mawb_airline: details.mawb_airline ?? null,
      };
      await http.post('/update-shipping-link', payload);
      emit('orderMoved', {
        success: true,
        action: 'shipping_details_saved',
        details,
      });
    } catch (error) {
      logError(error);
    }
    return;
  }
  activity_items.skip_saving_shipping_details =
    Boolean(details.skip_saving_shipping_details) && !orderRequiresShippingPersist();
  if (activity_items.skip_saving_shipping_details) {
    activity_items.shipping_company = null;
    activity_items.tracking_no = '';
    activity_items.tracking_link = null;
  } else {
    activity_items.shipping_company = details.shipping_company;
    activity_items.tracking_no = details.tracking_no;
    activity_items.tracking_link = details.tracking_link;
    activity_items.mawb_number = details.mawb_number ?? null;
    activity_items.mawb_airline = details.mawb_airline ?? null;
  }

  makeActivityData(props.order_item, props.order_item_index, ORDERSHIPPED, false);

  enterShippingDetails.value = false;

  emit('orderMoved', {
    success: true,
    action: 'shipping_details_saved',
    details,
  });
};

const submitActivity = async (submit_type: string, customer_approval: boolean = false) => {
  try {
    if (modal_type.value === 'test_sample') {
      if (customer_approval && Array.isArray(activity_items.activity_item_data)) {
        activity_items.order_item_id = props.order_item.id;
        activity_items.order_item_index = props.order_item_index;
        activity_items.order_move = 1;
        delete activity_items.order_item_status;
        delete activity_items.tracking_link;
        delete activity_items.tracking_no;
        delete activity_items.shipping_company;
        delete activity_items.reason;
        delete activity_items.order_move;
        // activity_items.order_item_status = activity_items.activity_item_data.every(
        //   (item) => item.status === CUSTOMERAPPROVED
        // )
        //   ? CUSTOMERAPPROVED
        //   : CUSTOMERREVIEW;
      } else {
        activity_items.activity_item_data =
          design_approval_activity_item_data.value ?? activity_items.activity_item_data;
      }
    }
    isMovingOrder.value = true;

    let form_data = new FormData();
    if (isManualActivity.value) {
      form_data.append('manual_activity', 'true');
    }
    form_data.append('order_id', String(props.order?.id || ''));

    for (const key in activity_items) {
      if (key === 'skip_saving_shipping_details') {
        continue;
      }
      if (key === 'activity_item_data' && Array.isArray(activity_items[key])) {
        activity_items[key].forEach((activity_file_obj: any, actIndx: number) => {
          for (const key2 in activity_file_obj) {
            if (key2 === 'files') {
              activity_file_obj[key2].forEach((activity_file: any, fileInd: number) => {
                for (const key3 in activity_file) {
                  if (key3 === 'file') {
                    form_data.append(
                      key + '[' + actIndx + '][' + key2 + '][' + fileInd + '][' + key3 + ']',
                      activity_file[key3] || ''
                    );
                  }
                  if (key3 === 'file_type') {
                    form_data.append(
                      key + '[' + actIndx + '][' + key2 + '][' + fileInd + '][' + key3 + ']',
                      activity_file[key3] || ''
                    );
                  }
                }
              });
            } else if (key2 === 'skip_customer_approval') {
              Object.entries(activity_file_obj[key2]).forEach(([skipkey, skip_value]) => {
                form_data.append(
                  key + '[' + actIndx + '][' + key2 + '][' + skipkey + ']',
                  skip_value as string
                );
              });
            } else {
              form_data.append(
                key + '[' + actIndx + '][' + key2 + ']',
                String(activity_file_obj[key2] || '')
              );
            }
          }
        });
      } else {
        form_data.append(key, String(activity_items[key] || ''));
      }
    }
    if (activity_items.skip_saving_shipping_details && !orderRequiresShippingPersist()) {
      form_data.append('skip_saving_shipping_details', '1');
    }

    let url = `customer-orders/${activity_items.order_item_id}/order-activity`;
    const headers =
      submit_type === 'form_data'
        ? {
            'Content-Type': 'multipart/form-data',
          }
        : {};
    if (customer_approval) {
      url = `customer-orders/${activity_items.order_item_id}/sample-activity`;
    }
    const response = await http.post(url, form_data, {
      headers,
    });

    const response_data = response.data;
    resetActivity();
    emit('orderMoved', response_data);
    if (response_data.result?.order_item) {
      emit('orderUpdated', response_data.result.order_item);
    }

    showToast('Order moved successfully', 'success');
  } catch (errorResponse) {
    console.error('Error submitting activity:', errorResponse);
    showToast('Error moving order. Please try again.', 'error');
  } finally {
    isMovingOrder.value = false;
    isManualActivity.value = false;
  }
};

const getSelectedMoveStatusValue = (): string => {
  const v = move_order_to_status.value;
  if (v == null) return '';
  return typeof v === 'object' && v !== null && 'value' in v ? v.value : String(v);
};

/** Suggested move reason when an admin pushes an item to QC (#20531). */
const PUSHED_TO_QC_REASON = 'Production completed';

/**
 * Pre-fill the move reason when "Pushed to QC" is selected.
 *
 * Pushing to QC always means the same thing — the goods are off the line and
 * ready for inspection — so the admin shouldn't have to retype it every time.
 *
 * Declared after getSelectedMoveStatusValue() on purpose: watch() evaluates its
 * getter immediately, so referencing the helper earlier would hit the temporal
 * dead zone and throw during setup.
 *
 * It's a suggestion, not a lock: anything the admin typed themselves is left
 * alone, and the default is cleared again if they switch to another status, so
 * it can never be submitted against a status it doesn't describe.
 */
watch(
  () => getSelectedMoveStatusValue(),
  (status) => {
    if (status === QUALITYCONTROL) {
      if (move_order_reason.value.trim() === '') {
        move_order_reason.value = PUSHED_TO_QC_REASON;
      }
    } else if (move_order_reason.value === PUSHED_TO_QC_REASON) {
      move_order_reason.value = '';
    }
  }
);

/** All three must be present to skip the move-order shipping confirmation modal. */
const orderItemHasCompleteTracking = (item: OrderDetailItem | null | undefined): boolean => {
  if (!item) return false;
  const co = item.shipping_company;
  const no = item.tracking_no;
  const link = item.tracking_link;
  const hasCompany = co != null && String(co).trim() !== '';
  const hasNo = no != null && String(no).trim() !== '';
  const hasLink = link != null && String(link).trim() !== '';
  return hasCompany && hasNo && hasLink;
};

/** Shipped / completed moves open the shipping modal only when tracking is incomplete. */
const needsShippingModalBeforeMove = (selectedStatus: string): boolean => {
  return selectedStatus === ORDERSHIPPED || selectedStatus === ORDERCOMPLETED;
};

const handleShippingModalClose = () => {
  showShippingModal.value = false;
  pendingMovePayload.value = null;
};

const handleShippingSubmit = (formData: ShippingDetailsFormData) => {
  const pending = pendingMovePayload.value;
  if (!pending) return;

  activity_items.skip_saving_shipping_details =
    Boolean(formData.skip_saving_shipping_details) && !orderRequiresShippingPersist();
  if (activity_items.skip_saving_shipping_details) {
    activity_items.shipping_company = null;
    activity_items.tracking_no = '';
    activity_items.tracking_link = null;
  } else {
    activity_items.shipping_company = formData.shipping_company;
    activity_items.tracking_no = formData.tracking_no;
    activity_items.tracking_link = formData.tracking_link;
  }

  showShippingModal.value = false;
  pendingMovePayload.value = null;

  makeActivityData(
    pending.order_item,
    pending.order_item_index,
    pending.selectedStatus,
    pending.add_files,
    pending.move_reason,
    true
  );

  move_order_to_status.value = null;
  move_order_reason.value = '';
};

const moveOrderStatus = (order_item: OrderDetailItem, order_item_index: number) => {
  activity_items.order_move = 1;
  activity_items.order_item_status = null;

  if (move_order_to_status.value === null) {
    showToast('Please select move order option', 'error');
    return false;
  }
  if (!move_order_reason.value) {
    showToast('Please state reason for moving the order', 'error');
    return false;
  }

  const selectedStatus = getSelectedMoveStatusValue();

  let add_files = false;

  if (selectedStatus === FACTORY_APPROVE_REJECT) {
    activity_items.order_item_status = ORDERAPPROVE;
    activity_navigation_index.value = 0;
    showArtworkDesigns(order_item, order_item_index, order_item.status_activities.length - 1);
    return;
  }
  if (selectedStatus === CUSTOMERREVIEW) {
    activity_items.order_item_status = CUSTOMERREVIEW;
    activity_navigation_index.value = 0;
    const activities = order_item.status_activities ?? [];
    if (activities.length === 0) {
      showToast('No status activities found for this order item.', 'error');
      return false;
    }
    const reviewIdx = activities.findIndex((a) => a.status === CUSTOMERREVIEW);
    const activityIdx = reviewIdx >= 0 ? reviewIdx : activities.length - 1;
    uploadSampleFiles(order_item, activityIdx, activities[activityIdx]);
    return;
  }
  if (selectedStatus === FACTORYREVIEW) {
    add_files = true;
  } else if (selectedStatus === ORDERAPPROVE) {
    add_files = true;
  } else if (selectedStatus === PRODUCTIONFILESUPLOADED) {
    uploadProductionFiles(order_item, order_item_index, selectedStatus);
    move_order_to_status.value = null;
    move_order_reason.value = '';
    return;
  }

  if (needsShippingModalBeforeMove(selectedStatus)) {
    if (orderItemHasCompleteTracking(order_item)) {
      const co = order_item.shipping_company;
      activity_items.shipping_company =
        co != null && String(co).trim() !== '' ? String(co).toLowerCase().trim() : null;
      activity_items.tracking_no =
        order_item.tracking_no != null ? String(order_item.tracking_no).trim() : '';
      activity_items.tracking_link =
        order_item.tracking_link != null ? String(order_item.tracking_link).trim() : null;

      makeActivityData(
        order_item,
        order_item_index,
        selectedStatus,
        add_files,
        move_order_reason.value,
        true
      );
      move_order_to_status.value = null;
      move_order_reason.value = '';
      return;
    }

    pendingMovePayload.value = {
      order_item,
      order_item_index,
      selectedStatus,
      add_files,
      move_reason: move_order_reason.value,
    };
    showShippingModal.value = true;
    return;
  }

  makeActivityData(
    order_item,
    order_item_index,
    selectedStatus,
    add_files,
    move_order_reason.value,
    true
  );
  move_order_to_status.value = null;
  move_order_reason.value = '';
};

const uploadProductionFiles = (
  order_item: any,
  order_item_index: number,
  selectedStatus: string
) => {
  activity_items.order_item_id = order_item.id;
  activity_items.order_item_index = order_item_index;
  activity_items.reason = move_order_reason.value || '';
  activity_items.order_item_status = selectedStatus;

  productionFilesItems.value = [];

  for (const factProd of order_item.factory_products) {
    const actObj = {
      action: null,
      status: selectedStatus,
      message: '',
      reason: activity_items.reason,
      manual_action: true,
      files: [],
      factory_product_id: factProd.id,
      product_files: [factProd.front_image, factProd.back_image],
    };

    const fileObj = {
      file: null,
      file_type: 'object',
      side: 'front',
    };
    actObj.files.push(fileObj);

    productionFilesItems.value.push(actObj);
  }
  activity_items.activity_item_data = productionFilesItems.value;

  showProductionFilesModal.value = true;
};

const openDeliveryNotes = async (delivery_note_id: number | null) => {
  showDeliveryNotesModal.value = true;
  await nextTick();
  if (deliveryNotesRef.value && typeof deliveryNotesRef.value.initDeliveryNotes === 'function') {
    deliveryNotesRef.value.initDeliveryNotes(delivery_note_id ?? null);
  }
};
const downloadDeliveryNotePDF = async (delivery_note_id: number) => {
  try {
    const { data } = await http.post(`/get-delivery-note-data`, {
      order_item_id: props.order_item.id,
      delivery_note_id,
    });
    let rows = [];
    let boxes = data?.delivery_boxes || [];
    const shippingLines = data?.lines || [];
    rows = shippingLines.map((line: Record<string, any>) => {
      const box = boxes.find((box_opt) => box_opt.id === line.box_id);
      return {
        line_id: line?.line_id ?? null,
        size: line?.size ?? null,
        has_name_number: line?.has_name_number ?? false,
        box: { label: box?.box_label ?? '', box_id: box?.box_id ?? null },
        factory_product_id: line?.factory_product_id,
        name: line.name ?? '',
        number: line.number ?? '',
        sku_number: line?.sku_number ?? '',
        product_name: line?.product_name ?? '',
        delivery_note_id: line?.delivery_note_id ?? '',
        qty_ordered: line?.qty_ordered ?? 0,
        qty_to_ship: line?.qty_to_ship ?? 0,
        qty_outstanding: line?.qty_outstanding ?? 0,
        already_shipped: line?.already_shipped ?? false,
        qty_original: line?.qty_original ?? 0,
        not_included: Boolean(line?.not_included),
        // New fields
        colors: line?.colors ?? [],
        style_id: line?.style_id ?? null,
        style_name: line?.style_name ?? null,
      };
    });
    const sortedRows = sortRowsByBox(rows);
    // Generate separate PDFs for each box
    const boxPdfs = generateBoxSeparatedPdfs(
      data.shipping_address?.edited ?? data.shipping_address?.original,
      sortedRows,
      {
        order_no: data.order_no,
        po_number: data.po_number,
      }
    );

    // Download each PDF
    boxPdfs.forEach(({ boxLabel, boxId, doc }) => {
      const sanitizedLabel = boxLabel.replace(/[^a-z0-9]/gi, '_');
      const boxIdPart = boxId ? `_${boxId}` : '';
      const orderNo = props.order?.order_no ?? props.order_item?.id ?? 'unknown';
      const filename = `order_${orderNo}_${sanitizedLabel}${boxIdPart}_delivery-note.pdf`;
      doc.save(filename);
    });
  } catch (e) {
    handleResponseException(e);
  }
};

const fetchNotes = async () => {
  if (!props.order_item) return;
  isFetchingNotes.value = true;
  notesError.value = null;
  try {
    const { data } = await http.get<DeliveryNotesResponse>(`/delivery-note`, {
      params: { order_item_id: props.order_item.id },
    });
    const list = data.delivery_notes;
    notes.value = Array.isArray(list) ? list : [];
    remainingCount.value = data.remaining;
    shippedCount.value = data.total_qty_shipped;
    orderedCount.value = data.total_qty_ordered;
  } catch (err) {
    handleResponseException(err);
    notesError.value = err;
    notes.value = [];
  } finally {
    isFetchingNotes.value = false;
  }
};
</script>

<style scoped>
.shipping-dates-list {
  padding-bottom: 1rem;
  border-left: 1px solid #4e4e4e;
  position: relative;
  padding-left: 20px;
}

.shipping-dates-list:last-child {
  border: 0px;
  padding-bottom: 0;
}

.shipping-dates-list:before {
  content: '';
  width: 5px;
  height: 5px;
  background: #4e4e4e;
  border: 1px solid #4e4e4e;
  transform: rotate(45deg);
  position: absolute;
  left: -3px;
  top: 3px;
}
</style>
